import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { htmlEncode } from '../../../utils/escape.utils';
import { MaintenanceWindowRebootOptionsData } from '../types';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import {
  PostponeRebootMinutesOptions,
  PostponeRebootOptions,
} from '../../../types/common/maintenanceWindowCommonTypes';
import { MaxLengthValidator } from '../../../ui/gzForm/validators/max-length.validator';
import { MaintenanceWindowsConstants } from '../../MaintenanceWindowsConstants';
import { TimeIntervalValidator } from '../../../ui/gzForm/validators/time-interval.validator';

export class MaintenanceWindowRebootOptionsView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');

    this.dom = {
      $title: this.shadow.querySelector('#reboot-preferences-title'),
      $notice: this.shadow.querySelector('#reboot-preferences-notice'),
      $noticeText: this.shadow.querySelector('#reboot-preferences-notice-text'),
      $radioGroupFormField: this.shadow.querySelector('#reboot-preferences-radio-options-form-field'),
      additionalOptions: {
        $rebootBetweenContainer: this.shadow.querySelector('#reboot-between'),
        $rebootBetweenStartFormField: this.shadow.querySelector('#between-start-form-field'),
        $rebootBetweenDescription: this.shadow.querySelector('#reboot-between-labeled-content gz-title'),
        $rebootBetweenEndFormField: this.shadow.querySelector('#between-end-form-field'),
        $rebootBetweenMinutesFormField: this.shadow.querySelector('#reboot-between-minutes-form-field'),
        $rebootBetweenMinutesPostDescription: this.shadow.querySelector('#post-description'),
        $rebootBetweenMinutesAfterDescription: this.shadow.querySelector('#after-description'),
        $rebootBetweenCustomMessageFormField: this.shadow.querySelector('#reboot-between-custom-message-form-field'),
        $rebootBetweenCustomMessageDescription: this.shadow.querySelector(
          '#reboot-between-custom-message-form-field gz-text'
        ),

        $rebootAfterContainer: this.shadow.querySelector('#reboot-after'),
        $rebootAfterMinutesFormField: this.shadow.querySelector('#reboot-after-minutes-form-field'),
        $rebootAfterMinutesDescription: this.shadow.querySelector('#reboot-after-minutes-form-field gz-text'),
        $rebootAfterCustomMessageFormField: this.shadow.querySelector('#reboot-after-custom-message-form-field'),
        $rebootAfterCustomMessageDescription: this.shadow.querySelector(
          '#reboot-after-custom-message-form-field gz-text'
        ),
      },
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$title.innerText = this.dependencies.translateFn('Reboot preferences');
    this.dom.$notice.innerText = this.dependencies.translateFn(
      'For users to view notifications and take actions when prompted, make sure the Endpoint restart notification and Display alert pop-ups options are enabled in the General > Notifications section of the policy settings.'
    );

    this.dom.$noticeText.innerHTML = this.dependencies.translateFn(
      'In case Endpoint restart notification and Display alert pop-ups are disabled and auto-restart is not selected, you need to manually send a restart task to the endpoints.'
    );

    this.dom.additionalOptions.$rebootBetweenDescription.innerText = this.dependencies.translateFn('and');
    this.dom.additionalOptions.$rebootBetweenMinutesPostDescription.innerText = this.dependencies.translateFn(
      'If the interval is missed, users can postpone the restart once for another'
    );
    this.dom.additionalOptions.$rebootBetweenMinutesAfterDescription.innerText =
      this.dependencies.translateFn('minutes');
    this.dom.additionalOptions.$rebootBetweenCustomMessageDescription.innerText = this.dependencies.translateFn(
      'Show the following message on the endpoint:'
    );
    this.dom.additionalOptions.$rebootBetweenCustomMessageFormField.Input.placeholder = this.dependencies.translateFn(
      'Customize the reboot message shown on the endpoint'
    );

    this.dom.additionalOptions.$rebootAfterMinutesDescription.innerText = this.dependencies.translateFn('minutes');
    this.dom.additionalOptions.$rebootAfterCustomMessageDescription.innerText = this.dependencies.translateFn(
      'Show the following message on the endpoint:'
    );
    this.dom.additionalOptions.$rebootAfterCustomMessageFormField.Input.placeholder = this.dependencies.translateFn(
      'Customize the reboot message shown on the endpoint'
    );
  }

  private setupRebootOptions() {
    const radioButtons = {
      first: this.shadow.querySelector('#reboot-preferences-radio-options-first'),
      second: this.shadow.querySelector('#reboot-preferences-radio-options-second'),
      third: this.shadow.querySelector('#reboot-preferences-radio-options-third'),
    };

    radioButtons.first.value = PostponeRebootOptions.CUSTOM_TIME.toString();
    radioButtons.first.label = htmlEncode(
      this.dependencies.translateFn('Users postpone the system restart until a more convenient time')
    );
    radioButtons.second.value = PostponeRebootOptions.INTERVAL.toString();
    radioButtons.second.label = htmlEncode(
      this.dependencies.translateFn('Users postpone the system restart only within a specific interval')
    );
    radioButtons.third.value = PostponeRebootOptions.AFTER.toString();
    radioButtons.third.label = htmlEncode(
      this.dependencies.translateFn('System restarts automatically after a specific number of minutes')
    );

    this.populateRebootDropdowns();
  }

  private populateRebootDropdowns() {
    const rebootAfterOptions = [
      {
        title: PostponeRebootMinutesOptions.TEN.toString(),
        value: PostponeRebootMinutesOptions.TEN.toString(),
      },
      {
        title: PostponeRebootMinutesOptions.THIRTY.toString(),
        value: PostponeRebootMinutesOptions.THIRTY.toString(),
      },
      {
        title: PostponeRebootMinutesOptions.FORTY_FIVE.toString(),
        value: PostponeRebootMinutesOptions.FORTY_FIVE.toString(),
      },
      {
        title: PostponeRebootMinutesOptions.SIXTY.toString(),
        value: PostponeRebootMinutesOptions.SIXTY.toString(),
      },
    ];

    this.dom.additionalOptions.$rebootAfterMinutesFormField.Input.setAttribute(
      'options',
      JSON.stringify(rebootAfterOptions)
    );
    this.dom.additionalOptions.$rebootBetweenMinutesFormField.Input.setAttribute(
      'options',
      JSON.stringify(rebootAfterOptions)
    );
  }

  buildSuccessView(data: { viewData: MaintenanceWindowRebootOptionsData; disabled: boolean }) {
    this.createFormGroup();

    this.setupRebootOptions();
    this.setInitialDataInForm(data.viewData);

    if (data.disabled) {
      this.setDisabledInnerComponents(data.disabled);
    }
  }

  setDisabledInnerComponents(disable: boolean) {
    this.dom.$radioGroupFormField.disabled = disable;
    for (const item of Object.keys(this.dom.additionalOptions)) {
      if (this.dom.additionalOptions[item] instanceof GzFormFieldWebComponent) {
        this.dom.additionalOptions[item].disabled = disable;
      }
    }

    if (!disable) {
      // restore reboot options component state
      this.dom.$radioGroupFormField.Input.dispatchEvent(
        new CustomEvent('triggerRevalidation', {
          detail: {},
          bubbles: true,
        })
      );
    }
  }

  private createFormGroup() {
    this.dom.additionalOptions.$rebootBetweenCustomMessageFormField.Validators = [
      MaxLengthValidator(
        MaintenanceWindowsConstants.RebootOptions.CustomMessageMaxLength,
        this.dependencies.translateFn('The message must have maximum {customMessageMaxLength} characters.', {
          customMessageMaxLength: MaintenanceWindowsConstants.RebootOptions.CustomMessageMaxLength,
        })
      ),
    ];
    this.dom.additionalOptions.$rebootAfterCustomMessageFormField.Validators = [
      MaxLengthValidator(
        MaintenanceWindowsConstants.RebootOptions.CustomMessageMaxLength,
        this.dependencies.translateFn('The message must have maximum {customMessageMaxLength} characters.', {
          customMessageMaxLength: MaintenanceWindowsConstants.RebootOptions.CustomMessageMaxLength,
        })
      ),
    ];
    this.dom.additionalOptions.$rebootBetweenEndFormField.Validators = [
      TimeIntervalValidator(
        this.dom.additionalOptions.$rebootBetweenStartFormField.Input,
        this.dependencies.translateFn('Start time has to be before end time.')
      ),
    ];

    // create FormGroup
    this.formGroup = new FormGroup({
      rebootOption: this.dom.$radioGroupFormField,
      additionalOptions: new FormGroup({
        interval: new FormGroup({
          between: new FormGroup({
            start: this.dom.additionalOptions.$rebootBetweenStartFormField,
            end: this.dom.additionalOptions.$rebootBetweenEndFormField,
          }),
          minutes: this.dom.additionalOptions.$rebootBetweenMinutesFormField,
          customMessage: this.dom.additionalOptions.$rebootBetweenCustomMessageFormField,
        }),
        after: new FormGroup({
          minutes: this.dom.additionalOptions.$rebootAfterMinutesFormField,
          customMessage: this.dom.additionalOptions.$rebootAfterCustomMessageFormField,
        }),
      }),
    });
  }

  private setInitialDataInForm(viewData: MaintenanceWindowRebootOptionsData) {
    this.formGroup.value = {
      rebootOption: viewData?.rebootOption || PostponeRebootOptions.CUSTOM_TIME.toString(),
      additionalOptions: {
        interval: {
          between: {
            start: viewData?.interval?.between?.start || {
              hours: 0,
              minutes: 0,
            },
            end: viewData?.interval?.between?.end || {
              hours: 23,
              minutes: 59,
            },
          },
          minutes: viewData?.interval?.minutes || PostponeRebootMinutesOptions.TEN.toString(),
          customMessage: viewData?.interval?.customMessage || '',
        },
        after: {
          minutes: viewData?.after?.minutes || PostponeRebootMinutesOptions.TEN.toString(),
          customMessage: viewData?.after?.customMessage || '',
        },
      },
    };
  }

  public getFormGroup(): FormGroup {
    return this.formGroup;
  }
}
